/**
 * RescheduleConfirmDialog.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Modal de confirmação de reagendamento.
 *
 * Exibe:
 *   - Avatar + nome do paciente
 *   - Procedimento · Profissional
 *   - Data atual → Nova data
 *   - Toggle "Enviar aviso ao paciente pelo WhatsApp"
 *   - Botões Cancelar / Confirmar Reagendamento
 *
 * @module RescheduleConfirmDialog
 */

import React, { useState, useEffect, useRef } from 'react';
import styles from './RescheduleConfirmDialog.module.css';

// Chave de localStorage para persistir preferência de WhatsApp
const KEY_SEND_WHATSAPP = 'sendAppointmentCreationWhatssappReminder';

// Nomes dos dias da semana em português (abreviados para o formato "Ter, 20 Mai")
const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS_SHORT  = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/**
 * Formata uma data para o padrão "Ter, 20 Mai às 14:00".
 *
 * @param {Date} date
 * @param {number} [hour]   - Sobrescreve a hora da data (opcional)
 * @param {number} [minute] - Sobrescreve o minuto da data (opcional)
 * @returns {string}
 */
function formatDateLabel(date, hour, minute) {
  if (!date) return '—';
  const d    = new Date(date);
  const h    = hour   !== undefined ? hour   : d.getHours();
  const m    = minute !== undefined ? minute : d.getMinutes();
  const day  = WEEKDAY_SHORT[d.getDay()];
  const num  = d.getDate();
  const mon  = MONTHS_SHORT[d.getMonth()];
  const hStr = String(h).padStart(2, '0');
  const mStr = String(m).padStart(2, '0');
  return `${day}, ${num} ${mon} às ${hStr}:${mStr}`;
}

/**
 * Extrai iniciais de um nome (máx. 2 letras).
 *
 * @param {string} name
 * @returns {string}
 */
function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ── Ícone WhatsApp (SVG inline) ───────────────────────────────────────────────
function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

// ── Ícone seta direita ────────────────────────────────────────────────────────
function ArrowRightIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

/**
 * Modal de confirmação de reagendamento.
 *
 * @param {Object}   props
 * @param {boolean}  props.isOpen          - Controla visibilidade
 * @param {Object}   props.appointment     - Agendamento original
 * @param {string}   props.patientName     - Nome do paciente (já resolvido)
 * @param {string}   props.procedureLabel  - Label do procedimento (já resolvido)
 * @param {string}   props.resourceLabel   - Label do profissional (já resolvido)
 * @param {Date}     props.newDate         - Objeto Date do novo dia selecionado
 * @param {number}   props.newHour         - Hora do novo slot
 * @param {number}   props.newMinute       - Minuto do novo slot
 * @param {Function} props.onConfirm       - (sendWhatsApp: boolean) => void
 * @param {Function} props.onCancel        - Fecha o dialog sem confirmar
 * @returns {React.ReactElement|null}
 */
export default function RescheduleConfirmDialog({
  isOpen,
  appointment,
  patientName,
  procedureLabel,
  resourceLabel,
  newDate,
  newHour,
  newMinute,
  onConfirm,
  onCancel,
}) {
  // ── Estado do toggle WhatsApp ─────────────────────────────────────────────
  const [sendWhatsapp, setSendWhatsapp] = useState(
    () => localStorage.getItem(KEY_SEND_WHATSAPP) === 'true'
  );

  // Persiste preferência no localStorage — pula o primeiro render
  // para não sobrescrever o valor salvo antes de o usuário interagir com o toggle.
  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    localStorage.setItem(KEY_SEND_WHATSAPP, String(sendWhatsapp));
  }, [sendWhatsapp]);

  // Relê preferência do localStorage ao abrir o dialog
  useEffect(() => {
    if (isOpen) {
      setSendWhatsapp(localStorage.getItem(KEY_SEND_WHATSAPP) === 'true');
    }
  }, [isOpen]);

  // Fecha ao pressionar Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onCancel]);

  if (!isOpen || !appointment) return null;

  // ── Dados formatados ──────────────────────────────────────────────────────
  const initials = getInitials(patientName);

  // Data original do agendamento
  const oldDateLabel = (() => {
    if (!appointment.date) return '—';
    const [datePart, timePart] = appointment.date.split(' ');
    const [y, mo, d] = datePart.split('-').map(Number);
    const [h, m]     = (timePart || '00:00').split(':').map(Number);
    const dateObj    = new Date(y, mo - 1, d, h, m, 0, 0);
    return formatDateLabel(dateObj);
  })();

  // Nova data selecionada
  const newDateLabel = formatDateLabel(newDate, newHour, newMinute);

  // Subtítulo: "Procedimento · Profissional"
  const subtitleParts = [procedureLabel, resourceLabel].filter(Boolean);
  const subtitle = subtitleParts.join(' · ');

  // ── Handlers ──────────────────────────────────────────────────────────────
  function handleConfirm() {
    onConfirm?.(sendWhatsapp);
  }

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) onCancel?.();
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className={styles.overlay}
      role="presentation"
      onClick={handleOverlayClick}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rcd-title"
      >
        {/* ── Cabeçalho ──────────────────────────────────────────────────── */}
        <div className={styles.dialogHeader}>
          <h2 className={styles.dialogTitle} id="rcd-title">
            Confirmar Reagendamento
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onCancel}
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        {/* ── Corpo ──────────────────────────────────────────────────────── */}
        <div className={styles.dialogBody}>

          {/* Paciente */}
          <div className={styles.patientRow}>
            <div className={styles.avatar} aria-hidden="true">
              {initials}
            </div>
            <div className={styles.patientInfo}>
              <span className={styles.patientName}>{patientName}</span>
              {subtitle && (
                <span className={styles.patientSubtitle}>{subtitle}</span>
              )}
            </div>
          </div>

          {/* Datas */}
          <div className={styles.datesCard}>
            <div className={styles.dateRow}>
              <span className={styles.dateRowLabel}>Data atual</span>
              <span className={styles.dateRowValue}>{oldDateLabel}</span>
            </div>
            <div className={styles.dateDivider} aria-hidden="true">
              <span className={styles.arrowIcon}>
                <ArrowRightIcon />
              </span>
            </div>
            <div className={`${styles.dateRow} ${styles.dateRowNew}`}>
              <span className={styles.dateRowLabel}>Nova data</span>
              <span className={`${styles.dateRowValue} ${styles.dateRowValueNew}`}>
                {newDateLabel}
              </span>
            </div>
          </div>

          {/* Toggle WhatsApp */}
          <div className={styles.whatsappRow}>
            <span className={styles.whatsappLabel}>
              <span className={styles.whatsappIcon}>
                <WhatsAppIcon />
              </span>
              Enviar aviso ao paciente pelo WhatsApp
            </span>
            <label className={styles.toggleWrapper}>
              <input
                type="checkbox"
                className={styles.toggleInput}
                checked={sendWhatsapp}
                onChange={(e) => setSendWhatsapp(e.target.checked)}
                aria-label="Enviar aviso pelo WhatsApp"
              />
              <span className={styles.toggleSlider} />
            </label>
          </div>

        </div>

        {/* ── Rodapé ─────────────────────────────────────────────────────── */}
        <div className={styles.dialogFooter}>
          <button
            type="button"
            className={styles.btnCancel}
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={styles.btnConfirm}
            onClick={handleConfirm}
          >
            Confirmar Reagendamento
          </button>
        </div>
      </div>
    </div>
  );
}
